/**
 * useDeliveryAttachmentPreview — the Deliver story preview card's attachment
 * as the real file: the chosen theme and format are rendered (the same
 * render the Export modal previews), so the card shows that deck's front
 * page and actual size.
 *
 * Only for a rendered deck (Presentation as PPTX/PDF); undefined otherwise,
 * so the card keeps its sample image set. Each open of the create view is a
 * new "delivery-<n>" session: switching theme/format back and forth within
 * one visit reuses renders, reopening renders fresh.
 */
import { useEffect, useState } from "react";
import { useExportPresentationPreview } from "./useExportPresentationPreview";
import { isThemedDeck } from "../components/organisms/DeliverStory/deliveryTheme";
import { toAttachmentPreview } from "../components/organisms/DeliverStory/deliveryAttachmentPreview";
import type {
  DeliveryAttachmentPreview,
  SendNowFormState,
} from "../components/organisms/DeliverStory/types";

export interface UseDeliveryAttachmentPreviewArgs {
  workspaceId: string | undefined;
  storyId: string | undefined;
  /** True while the create/edit view of Deliver story is open. */
  isOpen: boolean;
  form: Pick<SendNowFormState, "artifact" | "format">;
  /** Resolved theme (resolveDeliveryTheme); undefined until themes load. */
  themeKey: string | undefined;
  /** The theme list's error — without a theme there is nothing to render. */
  themesErrorMessage?: string | null;
}

export interface UseDeliveryAttachmentPreviewResult {
  attachment: DeliveryAttachmentPreview | undefined;
  /** The rendered file itself — lets Send now attach it without re-rendering. */
  downloadUrl: string | undefined;
  /** That file's artifact id; undefined until the render for the current
   *  theme/format is ready (so Send never attaches a stale render). */
  artifactId: string | undefined;
}

export function useDeliveryAttachmentPreview({
  workspaceId,
  storyId,
  isOpen,
  form,
  themeKey,
  themesErrorMessage = null,
}: UseDeliveryAttachmentPreviewArgs): UseDeliveryAttachmentPreviewResult {
  // Advance the session on close, not on open: the next open then starts on
  // a fresh key straight away, so it renders once (bumping on open would
  // fire one render for the old key and a second one for the new key).
  //
  // Seeded from Date.now(), not 0 — see usePresentationDeckSnapshot's
  // identical comment: a 0-based counter would collide with an earlier
  // mount's cached "delivery-0" query key after this hook remounts (e.g.
  // reopening Deliver Story later in the same browsing session), showing a
  // stale preview instead of one that reflects the story's current state.
  const [openCount, setOpenCount] = useState(() => Date.now());
  useEffect(() => {
    if (!isOpen) setOpenCount((n) => n + 1);
  }, [isOpen]);

  const themed = isThemedDeck(form);
  const format = form.format === "presentation" ? "pptx" : "pdf";
  const preview = useExportPresentationPreview({
    workspaceId,
    storyId,
    format,
    themeKey,
    sessionId: `delivery-${openCount}`,
    enabled: isOpen && themed,
    // This card only ever shows page 1 (see deliveryAttachmentPreview.ts),
    // so there's no reason to pay for rasterising/uploading every other page.
    previewPageLimit: 1,
  });

  if (!themed) return { attachment: undefined, downloadUrl: undefined, artifactId: undefined };
  const attachment = toAttachmentPreview(format, {
    ...preview,
    // Until the theme list resolves, the render hasn't started yet.
    isRendering: preview.isRendering || (!themeKey && !themesErrorMessage),
    errorMessage: themesErrorMessage ?? preview.errorMessage,
  });
  const ready = attachment.status === "ready";
  return {
    attachment,
    downloadUrl: ready ? preview.downloadUrl : undefined,
    artifactId: ready ? preview.artifactId : undefined,
  };
}
